import { isHalloweenBrandActive, REGULAR_SOCIAL_IMAGE, HALLOWEEN_SOCIAL_IMAGE } from "@shared/seasonal-brand";

export type SeasonalSiteTheme = "halloween";


const HALLOWEEN_MAIN_CATEGORY_ART: Record<string, string> = {
  "Bounce Houses": "/seasonal/halloween/bounce-house-photo.webp",
  "Water Slides": "/seasonal/halloween/water-slide-photo.webp",
  Combos: "/seasonal/halloween/construction-combo-photo.webp",
  "Obstacle Courses": "/seasonal/halloween/obstacle-courses-poster.webp",
  "Tables & Chairs": "/seasonal/halloween/tables-chairs-photo.webp",
  Concessions: "/seasonal/halloween/concessions-photo.webp",
  Games: "/seasonal/halloween/connect-four-photo.webp",
  Packages: "/seasonal/halloween/party-packages-photo.webp",
};

const HALLOWEEN_SECONDARY_CATEGORY_ART: Record<string, string> = {
  "all-party-rentals-tampa": "/seasonal/halloween/all-rentals-poster.webp",
  "deals-and-discounts-tampa": "/seasonal/halloween/deals-discounts-poster.webp",
  "bounce-slide-combos-tampa": "/seasonal/halloween/combos-poster.webp",
  "bounce-houses-tampa": "/seasonal/halloween/bounce-houses-poster.webp",
  "themed-bounce-houses-tampa": "/seasonal/halloween/themed-bounce-houses-poster.webp",
  "packages-and-specials-tampa": "/seasonal/halloween/specials-packages-poster.webp",
  "water-slides-tampa": "/seasonal/halloween/water-slides-poster.webp",
  "concessions-and-add-ons-tampa": "/seasonal/halloween/concessions-add-ons-poster.webp",
  "obstacle-courses-tampa": "/seasonal/halloween/obstacle-courses-poster.webp",
  "interactive-games-tampa": "/seasonal/halloween/interactive-games-poster.webp",
  "dunk-tanks-tampa": "/seasonal/halloween/dunk-tanks-poster.webp",
  "tables-and-chairs-tampa": "/seasonal/halloween/tables-chairs-poster.webp",
};

const HALLOWEEN_ROUTE_CATEGORY_ART: Record<string, string> = {
  "bounce-house-rentals": "/seasonal/halloween/bounce-house-photo.webp",
  "water-slide-rentals": "/seasonal/halloween/water-slide-photo.webp",
  "obstacle-course-rentals": "/seasonal/halloween/obstacle-courses-poster.webp",
  "combo-bouncers": "/seasonal/halloween/construction-combo-photo.webp",
  "table-and-chair-rentals": "/seasonal/halloween/tables-chairs-photo.webp",
  "concession-rentals": "/seasonal/halloween/concessions-photo.webp",
  "dunk-tank-rentals": "/seasonal/halloween/dunk-tanks-poster.webp",
  "game-rentals": "/seasonal/halloween/soccer-darts-photo.webp",
  "themed-bounce-houses": "/seasonal/halloween/themed-bounce-houses-poster.webp",
  "tent-rentals": "/seasonal/halloween/all-rentals-poster.webp",
};

function localPreviewTheme(): SeasonalSiteTheme | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  const isLocalDevelopmentHost = host === "localhost" || host === "127.0.0.1" || host.startsWith("192.168.");
  if (!isLocalDevelopmentHost) return null;
  return new URLSearchParams(window.location.search).get("seasonPreview") === "halloween" ? "halloween" : null;
}

export function getActiveSeasonalSiteTheme(now: Date = new Date()): SeasonalSiteTheme | null {
  const preview = localPreviewTheme();
  if (preview) return preview;
  return isHalloweenBrandActive(now) ? "halloween" : null;
}

export function applySeasonalSiteTheme(now: Date = new Date()): SeasonalSiteTheme | null {
  const theme = getActiveSeasonalSiteTheme(now);
  if (typeof document !== "undefined") {
    if (theme) document.documentElement.dataset.seasonalTheme = theme;
    else delete document.documentElement.dataset.seasonalTheme;
  }
  return theme;
}

export function seasonalMainCategoryArt(name: string, fallback: string): string {
  return getActiveSeasonalSiteTheme() === "halloween" ? HALLOWEEN_MAIN_CATEGORY_ART[name] || fallback : fallback;
}

export function seasonalSecondaryCategoryArt(slug: string, fallback: string | null | undefined): string | null {
  if (getActiveSeasonalSiteTheme() === "halloween") return HALLOWEEN_SECONDARY_CATEGORY_ART[slug] || fallback || null;
  return fallback || null;
}

export function seasonalRouteCategoryArt(slug: string, fallback: string): string {
  return getActiveSeasonalSiteTheme() === "halloween" ? HALLOWEEN_ROUTE_CATEGORY_ART[slug] || fallback : fallback;
}

export function seasonalHeroArt(): { desktop: string; mobile: string } | null {
  if (getActiveSeasonalSiteTheme() !== "halloween") return null;
  return {
    desktop: "/seasonal/halloween/pirate-ship-hero-desktop.webp",
    mobile: "/seasonal/halloween/pirate-ship-hero-mobile.webp",
  };
}

export function seasonalBrandLogo(fallback = "/grade-a-transparent-logo.webp"): string {
  return getActiveSeasonalSiteTheme() === "halloween"
    ? "/seasonal/halloween/grade-a-halloween-logo.webp"
    : fallback;
}

export function seasonalSocialImage(): string {
  return getActiveSeasonalSiteTheme() === "halloween" ? HALLOWEEN_SOCIAL_IMAGE : REGULAR_SOCIAL_IMAGE;
}
